import React, { useEffect, useState } from 'react';
import { useLocation, useParams, Link, Navigate } from 'react-router-dom';
import LandingNavbar from './Navbar';
import LandingFooter from './LandingFooter';
import ScrollReveal from './ScrollReveal';
import { useDemoModal } from './DemoModal';
import {
  RESOURCES, RESOURCE_ORDER, RESOURCE_CTA, SKELETON_WIDTHS,
} from './resourcesContent';
import './landing-animations.css';
import './landing-theme.css';
import './landing-eb.css';
import './landing-eb-resource.css';

// Brand icons: src/assets/images/eb/icons/<name>-<green|cream|gold>.png
const iconCtx = require.context('../../assets/images/eb/icons', false, /\.png$/);
const icon = (name, tone = 'green') => iconCtx(`./${name}-${tone}.png`);

/**
 * Shared template for the Resources pages.
 * Route: <Route path="/resources/:slug" element={<ResourcePage />} />
 * Content comes from resourcesContent.js:
 *   - heroImage: optional hero picture (otherwise the placeholder panel shows)
 *   - items:     optional real cards (otherwise skeleton placeholder tiles show)
 */
const ResourcePage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { openDemo: goDemo } = useDemoModal(); // "Book a demo" opens the demo form
  const [filter, setFilter] = useState(0); // index into r.filters; 0 = show all
  const r = RESOURCES[slug];

  // Start each resource page at the top, with the filter reset
  useEffect(() => {
    setFilter(0);
    if (!location.hash) window.scrollTo(0, 0);
  }, [slug, location.hash]);

  if (!r) return <Navigate to="/" replace />;

  const tiles = r.items || Array.from({ length: r.count }, (_, i) => ({
    tag: r.tag,
    w1: SKELETON_WIDTHS[i % 6],
    w2: SKELETON_WIDTHS[(i + 3) % 6],
  }));
  // Real items carry a `cat` that matches one of r.filters; placeholders ignore the filter
  const shown = r.items && filter > 0
    ? tiles.filter((t) => t.cat === r.filters[filter])
    : tiles;
  const others = RESOURCE_ORDER.filter((k) => k !== slug);

  return (
    <div className="landing-page eb min-vh-100 d-flex flex-column">
      <ScrollReveal />
      <LandingNavbar />

      {/* ── Hero ── */}
      <header className="eb-rs-hero">
        <div className="eb-rs-hero-inner">
          <div className="eb-rs-hero-copy">
            <nav className="eb-rs-crumbs" aria-label="Breadcrumb">
              <Link to="/#resources">Resources</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">{r.name}</span>
            </nav>
            <h1 className="eb-rs-h1">{r.name}</h1>
            <p className="eb-lead mb-0">{r.intro}</p>
            <div>
              <button type="button" className="landing-btn-primary" onClick={goDemo}>Book a demo</button>
            </div>
          </div>

          {r.heroImage ? (
            <div className="eb-rs-visual is-image">
              <img src={r.heroImage} alt={r.name} />
            </div>
          ) : (
            <div className="eb-rs-visual">
              <span className="eb-rs-visual-icon" aria-hidden="true"><img src={icon(r.icon, 'cream')} alt="" /></span>
              <div className="eb-rs-visual-note">
                <span className="tag">PLACEHOLDER · HERO VISUAL</span>
                <span>{r.heroNote}</span>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ── Content ── */}
      <section className="eb-section">
        <div className="eb-rs-content">
          <div className="eb-rs-content-head">
            <h2 className="eb-rs-h2">{r.listTitle}</h2>
            <div className="eb-rs-filters">
              {r.filters.map((f, i) => (
                r.items ? (
                  <button
                    key={f}
                    type="button"
                    className={i === filter ? 'is-on' : ''}
                    aria-pressed={i === filter}
                    onClick={() => setFilter(i)}
                  >
                    {f}
                  </button>
                ) : <span key={f}>{f}</span>
              ))}
            </div>
          </div>

          <div className="eb-rs-tiles">
            {shown.map((t, i) => (r.items ? (
              <div key={t.title} className="eb-rs-tile is-real">
                <div className="eb-rs-thumb is-icon" style={{ height: r.thumbH }}>
                  <img src={icon(t.icon)} alt="" />
                </div>
                <span className="tag">{t.cat.toUpperCase()}</span>
                <h3 className="eb-rs-tile-title">{t.title}</h3>
                <p className="eb-rs-tile-desc">{t.desc}</p>
                <p className="eb-rs-tile-skills">{t.skills.join(' · ')}</p>
              </div>
            ) : (
              <div key={`${t.tag}-${i}`} className="eb-rs-tile">
                <div className="eb-rs-thumb" style={{ height: r.thumbH }}>
                  <span>{r.thumbLabel}</span>
                </div>
                <span className="tag">{t.tag}</span>
                <i className="line l1" style={{ width: t.w1 }} />
                <i className="line l2" style={{ width: t.w2 }} />
                <i className="line l2" style={{ width: '55%' }} />
              </div>
            )))}
          </div>

          {!r.items && <p className="eb-rs-fine">Placeholder layout. Content for {r.name} to be supplied.</p>}
        </div>
      </section>

      {/* ── More resources ── */}
      <section className="eb-section eb-section-cream">
        <div className="eb-rs-more">
          <h2 className="eb-rs-h3">More resources</h2>
          <div className="eb-rs-more-grid">
            {others.map((k) => (
              <Link key={k} to={`/resources/${k}`} className="eb-rs-more-card">
                <img src={icon(RESOURCES[k].icon)} alt="" />
                <strong>{RESOURCES[k].name}</strong>
                <span>{RESOURCES[k].short}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Demo CTA ── */}
      <section id="demo" className="eb-section">
        <div className="eb-rs-cta">
          <h2 className="eb-h2">{RESOURCE_CTA.title}</h2>
          <p className="eb-lead mb-0">{RESOURCE_CTA.text}</p>
          <button type="button" className="landing-btn-primary" onClick={goDemo}>{RESOURCE_CTA.button}</button>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default ResourcePage;